import React, { useEffect, useMemo, useRef } from 'react'

import { useTranslation } from 'react-i18next'

// MUI Imports
import Typography from '@mui/material/Typography'
import Checkbox from '@mui/material/Checkbox'
import { Tooltip } from '@mui/material'

// Third-party Imports
import classnames from 'classnames'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues
} from '@tanstack/react-table'

// Component Imports
import { TablePaginationComponentServerSide } from '../molecules/TablePaginationComponent'
import BackdropLoading from '../atoms/BackdropLoading'

// Style Imports
import tableStyles from '../../styles/table.module.scss'

const httpPrefixer = (url) => {
  return (url?.indexOf('://') === -1) ? `http://${url}` : url
}

// Column Definitions
const columnHelper = createColumnHelper()

const FirmListServerSide = (props) => {
  const {
    serverSidePagination,
    setServerSidePaginationByKey,
    data,
    rowCount,
    fetching,
    handleSelectAllSectors,
    selectedSectors,
    languageGSD,
    className
  } = props
  const { t } = useTranslation()

  const skipPageResetRef = useRef()

  const columns = useMemo(
    () => {
      const columnsArr = [
        columnHelper.accessor('companyName', {
          header: t('mailTemplate.wizard.firmList.firmName'),
          cell: ({ row }) => (
            <div className='flex items-center gap-4'>
              <div className='flex flex-col'>
                <Typography color='text.primary' className='font-medium'>
                  {row.original.companyName}
                </Typography>
              </div>
            </div>
          )
        }),
        columnHelper.accessor('website', {
          header: t('mailTemplate.wizard.firmList.website'),
          cell: ({ row }) => (
            <div className='flex items-center gap-4'>
              <div className='flex flex-col' onClick={(e) => {
                e.stopPropagation()
              }}>
                <a href={httpPrefixer(row.original.website)} target="_blank" rel="noreferrer">
                  <Typography className='font-medium'>
                    {row.original.website}
                  </Typography>
                </a>
              </div>
            </div>
          )
        }),
        columnHelper.accessor('emailList', {
          header: t('mailTemplate.wizard.firmList.email'),
          cell: ({ row }) => (
            <div className='flex items-center gap-4'>
              <div className='flex flex-col'>
                <Typography color='text.primary' className='font-medium'>
                  {row.original.emailList?.length > 1
                    ? <Tooltip
                      title={
                        <div style={{ whiteSpace: 'pre-line' }}>
                          {row.original.emailList.slice(1, row.original.emailList.length).join('\n')}
                        </div>
                      }
                      arrow
                    >
                      <span>
                        {`${row.original.emailList?.[0]}...`}
                      </span>
                    </Tooltip>
                    : row.original.emailList?.[0]
                  }
                </Typography>
              </div>
            </div>
          )
        }),
        columnHelper.accessor('companySectors', {
          header: t('mailTemplate.wizard.firmList.sector'),
          cell: ({ row }) => {
            const sectorsArr = row.original.companySectors

            const displayText = `-${sectorsArr?.[0]?.code}-${sectorsArr?.[0]?.name?.[languageGSD]} ${sectorsArr?.length > 1 ? '...' : ''}`
            const tooltipText = sectorsArr?.length > 1
              ? sectorsArr
                .map((sector) => `-${sector.code}-${sector?.name?.[languageGSD] || ''}`)
                .join('\n')
              : null

            return (
              <div className="flex items-center gap-4">
                <Typography color="text.primary" className="font-medium">
                  <Tooltip
                    title={tooltipText && <div style={{ whiteSpace: 'pre-line' }}>{tooltipText}</div>}
                    arrow
                  >
                  <span>
                    {displayText}
                  </span>
                  </Tooltip>
                </Typography>
              </div>
            )
          }
        }),
        columnHelper.accessor('city', {
          header: t('mailTemplate.wizard.firmList.city'),
          cell: ({ row }) => (
            <div className='flex items-center gap-4'>
              <div className='flex flex-col'>
                <Typography color='text.primary' className='font-medium'>
                  {row.original.city}
                </Typography>
              </div>
            </div>
          )
        }),
        columnHelper.accessor('companyType', {
          header: t('mailTemplate.wizard.firmList.companyType'),
          cell: ({ row }) => (
            <div className='flex items-center gap-4'>
              <div className='flex flex-col'>
                <Typography color='text.primary' className='font-medium'>
                  {row.original.companyType}
                </Typography>
              </div>
            </div>
          )
        }),
        columnHelper.accessor('incorporationDate', {
          header: t('mailTemplate.wizard.firmList.incorporationDate'),
          cell: ({ row }) => (
            <div className='flex items-center gap-4'>
              <div className='flex flex-col'>
                <Typography color='text.primary' className='font-medium'>
                  {row.original.incorporationDate}
                </Typography>
              </div>
            </div>
          )
        }),
        columnHelper.accessor('zipCode', {
          header: t('mailTemplate.wizard.firmList.zipCode'),
          cell: ({ row }) => (
            <div className='flex items-center gap-4'>
              <div className='flex flex-col'>
                <Typography color='text.primary' className='font-medium'>
                  {row.original.zipCode}
                </Typography>
              </div>
            </div>
          )
        }),
        columnHelper.accessor('phoneNumber', {
          header: t('mailTemplate.wizard.firmList.phone'),
          cell: ({ row }) => (
            <div className='flex items-center gap-4'>
              <div className='flex flex-col'>
                <Typography color='text.primary' className='font-medium'>
                  {row.original.phoneNumber}
                </Typography>
              </div>
            </div>
          )
        }),
        columnHelper.accessor('companyProfile', {
          header: t('mailTemplate.wizard.firmList.profile'),
          cell: ({ row }) => (
            <div className='flex items-center gap-4'>
              <div className='flex flex-col'>
                <Typography color='text.primary' className='font-medium'>
                  {row.original.companyProfile}
                </Typography>
              </div>
            </div>
          )
        })
      ]

      if (handleSelectAllSectors) {
        columnsArr.unshift({
          id: 'select',
          header: ({ table }) => (
          <Checkbox
            {...{
              checked: !!selectedSectors.length,
              onChange: (e) => { handleSelectAllSectors(e.target.checked) }
            }}
          />
          ),
          cell: ({ row }) => (
          <Checkbox
            {...{
              checked: !!selectedSectors.length,
              disabled: true
            }}
          />
          )
        })
      }

      return columnsArr
    },
    [t, selectedSectors]
  )

  const table = useReactTable({
    data,
    rowCount,
    columns,
    state: {
      serverSidePagination
    },
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    autoResetPageIndex: !skipPageResetRef.current,
    autoResetExpanded: !skipPageResetRef.current,
    getRowId: row => row.id
    // debugTable: true
  })

  useEffect(() => {
    // https://tanstack.com/table/latest/docs/faq#how-do-i-stop-my-table-state-from-automatically-resetting-when-my-data-changes
    skipPageResetRef.current = false
  })

  return (
    <div className='relative'>
      <div className='overflow-x-auto'>
        <table className={className ?? tableStyles.table}>
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())
                    }
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          {table.getFilteredRowModel().rows.length === 0
            ? (
                <tbody>
                  <tr>
                    <td colSpan={table.getVisibleFlatColumns().length} className='text-center'>
                      {t('mailTemplate.wizard.firmList.noData')}
                    </td>
                  </tr>
                </tbody>
              )
            : (
                <tbody>
                  {table
                    .getRowModel()
                    .rows.map(row => {
                      return (
                        <tr
                          key={row.id}
                          className={classnames({ selected: row.getIsSelected() })}
                          style={{ cursor: 'pointer' }}
                          onClick={row.getToggleSelectedHandler()}
                        >
                          {row.getVisibleCells().map(cell => (
                            <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                          ))}
                        </tr>
                      )
                    })}
                </tbody>
              )
          }
        </table>
      </div>
      <TablePaginationComponentServerSide
        infoText1={t('mailTemplate.wizard.firmList.tableInfo_1')}
        infoText2={t('mailTemplate.wizard.firmList.tableInfo_2')}
        count ={rowCount}
        pageIndex ={serverSidePagination?.pageIndex || 0}
        pageSize = {serverSidePagination?.pageSize || 10}
        setPageIndex={(val) => { setServerSidePaginationByKey({ ...serverSidePagination, pageIndex: val }) }}
      />
      <BackdropLoading open={fetching}/>
    </div>
  )
}

export default FirmListServerSide
